import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { isHeatmapPositionColumn } from '../../utils/imageDensityPoints';
import { resolveIntervalColumns } from '../../utils/intervalChart';
import {
  TOOLTIP_DETAIL_CHART_TYPES,
  describeTooltipFilter,
  filterRowsByTooltipValues,
  normalizeTooltipDetail,
} from '../../utils/tooltipDetail';
import TooltipDetailChart from './TooltipDetailChart';

const MAX_SAMPLE_VALUES = 200;

const emptyChart = {
  type: '',
  xColumn: '',
  yColumn: '',
  labelColumn: '',
  startColumn: '',
  endColumn: '',
  seriesColumn: '',
  columns: [],
};

function ColumnSelect({ label, value, onChange, columns, allowEmpty = false, emptyLabel = '(ninguna)' }) {
  return (
    <FormControl size="small" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value || ''} onChange={(e) => onChange(e.target.value)}>
        {allowEmpty && <MenuItem value="">{emptyLabel}</MenuItem>}
        {columns.map((c) => (
          <MenuItem key={c} value={c}>
            {c}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function cellKey(value) {
  if (value == null) return '';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

function initialDraft(value, rows, columns, defaultFilterColumn) {
  const normalized = normalizeTooltipDetail(value);
  if (normalized) return normalized;
  const filterColumn = columns.includes(defaultFilterColumn) ? defaultFilterColumn : columns[0] || '';
  const guess = resolveIntervalColumns(rows, {});
  const canIntervals = Boolean(guess.startColumn && guess.endColumn);
  return {
    filterColumn,
    targetColumn: filterColumn,
    chart: {
      ...emptyChart,
      type: canIntervals ? 'intervals' : 'table',
      startColumn: canIntervals ? guess.startColumn : '',
      endColumn: canIntervals ? guess.endColumn : '',
    },
  };
}

function isDraftValid(draft) {
  const c = draft?.chart || {};
  if (!draft?.filterColumn || !c.type) return false;
  if (c.type === 'intervals') return Boolean(c.startColumn && c.endColumn);
  if (c.type === 'table') return true;
  return Boolean(c.xColumn && c.yColumn);
}

/**
 * Configura qué muestra el tooltip de un marcador: filtra la tabla que alimenta el
 * heatmap por un campo del marcador y lo dibuja con un tipo de gráfico existente.
 */
export default function TooltipDetailEditorDialog({
  open,
  onClose,
  value,
  onSave,
  data = [],
  defaultFilterColumn = '',
  columnDurationFormats,
  columnDateFormats,
  columnDateBuckets,
}) {
  const columns = useMemo(() => {
    const sample = Array.isArray(data) ? data.find((r) => r && typeof r === 'object') : null;
    return sample ? Object.keys(sample).filter((k) => !isHeatmapPositionColumn(k)) : [];
  }, [data]);

  const [draft, setDraft] = useState(() => initialDraft(value, data, columns, defaultFilterColumn));
  const [sampleKey, setSampleKey] = useState('');

  useEffect(() => {
    if (open) {
      setDraft(initialDraft(value, data, columns, defaultFilterColumn));
      setSampleKey('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sampleValues = useMemo(() => {
    if (!draft.filterColumn) return [];
    const seen = new Map();
    for (const row of data || []) {
      if (!row || !Object.prototype.hasOwnProperty.call(row, draft.filterColumn)) continue;
      const v = row[draft.filterColumn];
      const k = cellKey(v);
      if (!seen.has(k)) seen.set(k, v);
      if (seen.size >= MAX_SAMPLE_VALUES) break;
    }
    return [...seen.entries()].map(([key, raw]) => ({ key, raw }));
  }, [data, draft.filterColumn]);

  const activeSample = sampleValues.find((s) => s.key === sampleKey) || sampleValues[0] || null;
  const previewRows = useMemo(
    () =>
      activeSample
        ? filterRowsByTooltipValues(data, draft.filterColumn, [activeSample.raw])
        : [],
    [data, draft.filterColumn, activeSample]
  );

  const patch = (next) => setDraft((prev) => ({ ...prev, ...next }));
  const patchChart = (next) => setDraft((prev) => ({ ...prev, chart: { ...prev.chart, ...next } }));
  const chart = draft.chart || emptyChart;
  const valid = isDraftValid(draft);
  const isPie = chart.type === 'pie' || chart.type === 'doughnut';

  const toggleTableColumn = (col) => {
    const cur = new Set(chart.columns || []);
    if (cur.has(col)) cur.delete(col);
    else cur.add(col);
    patchChart({ columns: columns.filter((c) => cur.has(c)) });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>Editar tooltip</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!columns.length ? (
          <Typography variant="body2" color="text.secondary">
            Carga datos en la vista previa para elegir las columnas del tooltip.
          </Typography>
        ) : (
          <>
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 1 }}>
                Filtro
              </Typography>
              <Box sx={{ maxWidth: 360 }}>
                <ColumnSelect
                  label="Campo de filtro"
                  value={draft.filterColumn}
                  columns={columns}
                  onChange={(v) => patch({ filterColumn: v, targetColumn: v })}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                El valor de esta columna en el marcador filtra la misma columna de la tabla base. En un cluster se
                usan todos sus valores.
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 1 }}>
                Subgráfico
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select label="Tipo" value={chart.type} onChange={(e) => patchChart({ type: e.target.value })}>
                    {TOOLTIP_DETAIL_CHART_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {chart.type === 'intervals' && (
                  <>
                    <ColumnSelect label="Inicio" value={chart.startColumn} columns={columns} onChange={(v) => patchChart({ startColumn: v })} />
                    <ColumnSelect label="Fin" value={chart.endColumn} columns={columns} onChange={(v) => patchChart({ endColumn: v })} />
                    <ColumnSelect
                      label="Serie"
                      value={chart.seriesColumn}
                      columns={columns}
                      allowEmpty
                      onChange={(v) => patchChart({ seriesColumn: v })}
                    />
                  </>
                )}
                {chart.type && chart.type !== 'intervals' && chart.type !== 'table' && (
                  <>
                    <ColumnSelect
                      label={isPie ? 'Leyenda' : 'Eje X'}
                      value={chart.xColumn}
                      columns={columns}
                      onChange={(v) => patchChart({ xColumn: v })}
                    />
                    <ColumnSelect
                      label={isPie ? 'Valor' : 'Eje Y'}
                      value={chart.yColumn}
                      columns={columns}
                      onChange={(v) => patchChart({ yColumn: v })}
                    />
                    {!isPie && (
                      <ColumnSelect
                        label="Serie"
                        value={chart.labelColumn}
                        columns={columns}
                        allowEmpty
                        onChange={(v) => patchChart({ labelColumn: v })}
                      />
                    )}
                  </>
                )}
              </Box>
              {chart.type === 'table' && (
                <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                    Columnas visibles (ninguna seleccionada = todas)
                  </Typography>
                  {columns.map((col) => {
                    const selected = (chart.columns || []).includes(col);
                    return (
                      <Chip
                        key={col}
                        size="small"
                        label={col}
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        onClick={() => toggleTableColumn(col)}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>

            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <FormControl size="small" sx={{ minWidth: 220 }} disabled={!sampleValues.length}>
                  <InputLabel>Vista previa con</InputLabel>
                  <Select
                    label="Vista previa con"
                    value={activeSample?.key ?? ''}
                    onChange={(e) => setSampleKey(e.target.value)}
                  >
                    {sampleValues.map((s) => (
                      <MenuItem key={s.key} value={s.key}>
                        {s.key || '(vacío)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {activeSample && (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={describeTooltipFilter(draft.filterColumn, [activeSample.raw])}
                  />
                )}
              </Box>
              {valid ? (
                <TooltipDetailChart
                  detail={draft}
                  rows={previewRows}
                  columnDurationFormats={columnDurationFormats}
                  columnDateFormats={columnDateFormats}
                  columnDateBuckets={columnDateBuckets}
                  onStyleChange={(style) => patchChart({ style })}
                  minHeight={240}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  Completa las columnas del subgráfico para ver la vista previa.
                </Typography>
              )}
            </Paper>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={!valid}
          onClick={() => {
            onSave?.(normalizeTooltipDetail(draft));
            onClose?.();
          }}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
